import { isCalendarDay } from './agenda-calendar'

/** Les deux vues de l'agenda, dans les mots de la maquette validée. */
export const AGENDA_VIEWS = ['week', 'month'] as const

export type AgendaView = (typeof AGENDA_VIEWS)[number]

/**
 * La vue et la période affichées, **dans l'URL** et non dans un `useState` d'écran.
 *
 * Ouvrir une fiche démonte l'agenda — l'éditeur est une route sœur, pas un panneau. Un état de
 * composant repartirait donc sur la semaine d'aujourd'hui à chaque retour, et l'agent qui prépare
 * la semaine prochaine y reviendrait à la main à chaque contrôle consulté. La période survit du
 * même coup à un rechargement, ce qu'une tablette fait pour de vrai (D22).
 */
export interface AgendaSearch {
  view: AgendaView
  /**
   * Le jour d'ancrage de la période, ou **la chaîne vide pour « aujourd'hui »**.
   *
   * ⚠️ Vide et non la date du jour : la valeur par défaut d'un paramètre de recherche est
   * comparée telle quelle pour être retirée de l'URL (`stripSearchParams`), et une date qui
   * change à minuit ne peut pas servir de défaut. `/agenda` reste donc `/agenda`, et l'écran
   * résout « aujourd'hui » à l'affichage — au bon jour, même si l'onglet est resté ouvert.
   */
  day: string
}

export const AGENDA_DEFAULTS: AgendaSearch = { view: 'week', day: '' }

/**
 * Lit la période d'une URL **sans jamais refuser d'entrer** (M21) : un `?day=2026-13-45` venu
 * d'un lien tordu ramène l'agent à aujourd'hui plutôt que de le laisser devant une erreur au
 * milieu de sa tournée.
 */
export function validateAgendaSearch(search: Record<string, unknown>): AgendaSearch {
  const view = search.view
  const day = search.day

  return {
    view: AGENDA_VIEWS.includes(view as AgendaView) ? (view as AgendaView) : AGENDA_DEFAULTS.view,
    day: typeof day === 'string' && isCalendarDay(day) ? day : AGENDA_DEFAULTS.day,
  }
}
